/**
 * Setup Guard
 *
 * Ensures the user has:
 * 1. An active ledger (organization) selected
 * 2. Fiscal periods configured
 * 3. Company details complete (entity linked, GSTIN set, state set)
 *
 * Redirects to appropriate setup pages if requirements are not met.
 */

import React from 'react'

import { useLocation } from 'react-router-dom'

import { CurrentPeriodRequired } from './SetupGuard-current-period'
import { useSetupGuardState } from './SetupGuard-hooks'
import { FiscalPeriodsRequired, LoadingSpinner, isExemptPath } from './SetupGuard-shared'
import { useLedgerContext } from '../stores/ledger-store'
import { useOrganization } from '../stores/organization-store'

interface SetupGuardProps {
  children: React.ReactNode
}

export function SetupGuard({ children }: SetupGuardProps): React.ReactElement | null {
  const location = useLocation()
  const { isInitializing, organizationId } = useOrganization()
  const { isLoaded, isLoading } = useLedgerContext()
  const {
    hasLedgers,
    isCheckingPeriods,
    hasPeriods,
    hasCurrentPeriod,
    retriggerCheck,
  } = useSetupGuardState()
  const isExempt = isExemptPath(location.pathname)

  if (isInitializing || !isLoaded || isLoading) { return <LoadingSpinner message="Loading your workspace..." /> }
  if (!hasLedgers && !isExempt) { return null }
  if (isCheckingPeriods) { return <LoadingSpinner message="Checking fiscal periods..." /> }
  if (!isExempt) {
    if (hasPeriods === false) { return <FiscalPeriodsRequired /> }
    if (hasCurrentPeriod === false) {
      return <CurrentPeriodRequired orgId={organizationId} onSuccess={retriggerCheck} />
    }
  }
  return children as React.ReactElement
}
